import { useEffect, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { FullScreenLoader } from '@/components/common/FullScreenLoader';
import { Icon } from '@/components/common/Icon';
import { Button } from '@/components/ui/button';
import { LEGAL_DOCUMENT_PATHS, LEGAL_GATE_BODY_KEYS } from '@/constants/legal';
import { resolveLegalAudienceForDocuments } from '@/lib/resolveLegalAudience';
import { LEGAL_ACCEPTANCE_PENDING_EVENT_NAME } from '@/lib/legalAcceptanceEvents';
import { useAcceptLegalDocumentsMutation, usePendingLegalDocumentsQuery } from '@/hooks/useLegal';

const DOCUMENT_TITLE_KEYS = {
  privacy_notice: 'privacyNotice',
  terms_of_service: 'termsOfService',
  data_processing_agreement: 'dataProcessingAgreement',
  client_portal_terms: 'clientPortalTerms',
} as const;

interface LegalAcceptanceGateProps {
  children: ReactNode;
}

export function LegalAcceptanceGate({ children }: LegalAcceptanceGateProps) {
  const { t } = useTranslation('legal');
  const pendingQuery = usePendingLegalDocumentsQuery();
  const acceptMutation = useAcceptLegalDocumentsMutation();
  const { refetch: refetchPending } = pendingQuery;

  useEffect(
    function refetchWhenTheServerReportsPendingDocuments() {
      function refetchPendingDocuments() {
        void refetchPending();
      }
      window.addEventListener(LEGAL_ACCEPTANCE_PENDING_EVENT_NAME, refetchPendingDocuments);
      return function stopListening() {
        window.removeEventListener(LEGAL_ACCEPTANCE_PENDING_EVENT_NAME, refetchPendingDocuments);
      };
    },
    [refetchPending]
  );

  if (pendingQuery.isLoading) {
    return <FullScreenLoader />;
  }
  const pendingDocuments = pendingQuery.data === undefined ? [] : pendingQuery.data;
  if (pendingDocuments.length === 0) {
    return <>{children}</>;
  }
  const audience = resolveLegalAudienceForDocuments(
    pendingDocuments.map(function readCode(document) {
      return document.code;
    })
  );

  return (
    <div className="min-h-screen bg-zoooffwhite flex items-center justify-center p-4">
      <div className="w-full max-w-lg bg-zoowhite border border-zooborder rounded-3xl shadow-xl p-7 md:p-9">
        <div className="w-14 h-14 rounded-2xl bg-zoovpalelavender text-zooviolet flex items-center justify-center mb-4">
          <Icon name="FileText" className="text-xl" />
        </div>
        <h1 className="text-xl font-bold text-zooindigo">{t('gate.title')}</h1>
        <p className="text-sm text-zoomuted mt-2">{t(LEGAL_GATE_BODY_KEYS[audience])}</p>
        <ul className="mt-4 space-y-2">
          {pendingDocuments.map(function renderDocument(document) {
            return (
              <li key={document.code}>
                <a
                  href={LEGAL_DOCUMENT_PATHS[document.code]}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center justify-between rounded-xl border border-zooborder px-4 py-3 text-sm font-medium text-zooindigo hover:border-zooprimary"
                >
                  {t(DOCUMENT_TITLE_KEYS[document.code])}
                  <Icon name="ChevronRight" className="text-zoomuted" />
                </a>
              </li>
            );
          })}
        </ul>
        {acceptMutation.isError ? (
          <p role="alert" className="mt-3 text-sm text-zooerrortext">
            {t('gate.error')}
          </p>
        ) : null}
        <Button
          size="large"
          className="mt-6 w-full"
          isLoading={acceptMutation.isPending}
          onClick={function acceptDocuments() {
            acceptMutation.mutate(pendingDocuments);
          }}
        >
          {t('gate.accept')}
        </Button>
      </div>
    </div>
  );
}
